import { QueryMethod } from "@/hooks/usePaginateQuery";

export type ConditionOption = {
  name: string;
  /**
   * Optional human-friendly label shown in the UI. Falls back to `name` when
   * absent. Never sent to the API — `name` remains the serialized identifier.
   */
  displayName?: string;
  id: string;
  type?: string;
};

export type AttributeValueType =
  | "text"
  | "text-area"
  | "single-select"
  | "multi-select";

/**
 * Typed config for dynamically loading a list of options from an API method.
 *
 * @template Args     - The params type accepted by the API method.
 * @template Response - The array response type returned by the API method.
 * @template Output   - The shape each item is mapped to (default: ConditionOption).
 *
 * Example:
 *   fetchOptions: {
 *     method: CountryControllerService.listCountriesUsingGet,
 *     params: { accountId },
 *     parseOption: (item) => ({ id: item.code, name: item.label }),
 *   }
 */
export type FetchOptionsConfig<
  Args = unknown,
  Response extends unknown[] = unknown[],
  Output = ConditionOption,
> = {
  method: QueryMethod<Args, Response>;
  params?: Args;
  parseOption: (item: Response[number]) => Output;
};

/**
 * Config for server-side paginated + filterable value options.
 * When set, ConditionValueField renders FormDropDownListPaginate instead of
 * FormMultiSelect, enabling lazy load on open and server-side search.
 *
 * Pass the raw API method directly — ConditionValueField wraps it internally
 * to apply `parseOption` and filter out null results before handing data to
 * FormDropDownListPaginate.
 *
 * Note: `method` and `parseOption` use `any` for their parameter types.
 * TypeScript's `strictFunctionTypes` makes function params contravariant, so
 * a specific API method `(params: { id: string }) => ...` is not assignable
 * to `(params: Record<string, unknown>) => ...`, and a narrowly-typed
 * `parseOption: (item: SpecificType) => ...` is not assignable to
 * `(item: unknown) => ...`. TypeScript's bivariance exemption for method
 * declarations (`method(x: T): R`) only applies during structural type
 * comparison, not when assigning function values — so it does not help here.
 * `any` is the only way to accept real API methods and caller-typed
 * `parseOption` functions without forcing explicit casts at every call site.
 *
 * Example:
 *   paginatedFetchOptions: {
 *     method: ValuesService.getValuesUsingGet,
 *     params: { attributeId },
 *     parseOption: item => item.value ? { id: item.value, name: item.value } : null,
 *   }
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PaginatedFetchOptionsConfig<Item = any> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  method: (params: any) => Promise<{ items?: Item[]; total?: number }>;
  params: Record<string, unknown>;
  /** Map a raw API item to a ConditionOption. Return null to skip the item. */
  parseOption: (item: Item) => ConditionOption | null;
  perPage?: number;
  sort?: { field: string; dir: "asc" | "desc" };
};

/**
 * Resolved metadata for a specific attribute, returned by `resolveAttribute`.
 * All fields are optional — only override what differs from the global config.
 */
export type ResolveAttributeResult = {
  /** Overrides config.operators for this attribute. */
  operators?: ConditionOption[];
  valueType?: AttributeValueType;
  /** Static value options (used when valueType is single/multi select). */
  valueOptions?: ConditionOption[];
  /**
   * Dynamic value options fetch config.
   * When present, takes priority over valueOptions.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchOptions?: FetchOptionsConfig<any, any[]>;
  /**
   * Server-side paginated fetch config.
   * When present, takes priority over fetchOptions and valueOptions.
   * Renders FormDropDownListPaginate with lazy load on open and debounced search.
   */
  paginatedFetchOptions?: PaginatedFetchOptionsConfig;
  /**
   * Match With options for this attribute.
   * Only used when config.enableMatchWith is true.
   * Used as a fallback when resolveOperator does not return matchWithOptions.
   */
  matchWithOptions?: MatchWithOption[];
};

/**
 * Resolved value configuration for a specific (attribute, operator) pair,
 * returned by `resolveOperator`. Only value-related fields are supported —
 * operators cannot be changed here (that would be circular).
 *
 * All fields are optional — only override what differs from the attribute-level config.
 */
export type ResolveOperatorResult = {
  valueType?: AttributeValueType;
  /** Static value options (used when valueType is single/multi select). */
  valueOptions?: ConditionOption[];
  /**
   * Dynamic value options fetch config.
   * When present, takes priority over valueOptions.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchOptions?: FetchOptionsConfig<any, any[]>;
  /**
   * Server-side paginated fetch config.
   * When present, takes priority over fetchOptions and valueOptions.
   * Renders FormDropDownListPaginate with lazy load on open and debounced search.
   */
  paginatedFetchOptions?: PaginatedFetchOptionsConfig;
  /**
   * Match With options for this (attribute, operator) pair.
   * Only used when config.enableMatchWith is true.
   * Takes priority over resolveAttribute and the static attribute list.
   */
  matchWithOptions?: MatchWithOption[];
};

/**
 * A "Match With" option (used when config.enableMatchWith is true). Selecting
 * it in the Match With dropdown drives how the Value field renders for that
 * row. Each option carries its own value config: when it sets
 * valueType/valueOptions/fetchOptions/paginatedFetchOptions, those take the
 * HIGHEST priority for the Value field (above resolveOperator). When a field
 * is omitted, the Value field falls back through the normal chain.
 *
 * The API valueTypes (CONSTANT, COLUMN_TAGGED_AS, SESSION_ATTRIBUTE,
 * CUSTOM_EXPRESSION, FUNCTION) are semantic categories, not render types — the
 * consumer maps each to one of the generic primitives below. For example:
 *   { id: "CONSTANT", name: "Constant Value", valueType: "text" }   // -> text input
 *   { id: "COLUMN_TAGGED_AS", name: "Column Name",
 *     valueType: "single-select", fetchOptions: {...} }             // -> dropdown
 */
export type MatchWithOption = ConditionOption & {
  valueType?: AttributeValueType;
  /** Static select options for this Match With option. */
  valueOptions?: ConditionOption[];
  /** Dynamic options fetch config. Takes priority over valueOptions. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchOptions?: FetchOptionsConfig<any, any[]>;
  /** Server-side paginated fetch config. Highest priority within this option. */
  paginatedFetchOptions?: PaginatedFetchOptionsConfig;
};

export type ConditionAttributeOption = ConditionOption & {
  /**
   * Per-attribute static operator override.
   * Overrides config.operators for this specific attribute.
   * e.g. a date attribute might expose "before" / "after" instead of "contains".
   */
  operators?: ConditionOption[];
  valueType?: AttributeValueType;
  /** Static list of select options. Used when options don't require an API call. */
  valueOptions?: ConditionOption[];
  /**
   * Dynamic options loaded from an API method.
   * Called when this attribute is selected; shows a loading state while fetching.
   * Use `parseOption` to map the raw API response items to `{ id, name }`.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchOptions?: FetchOptionsConfig<any, any[]>;
  /**
   * Static Match With options for this attribute.
   * Only used when config.enableMatchWith is true.
   * Lowest priority — overridden by resolveAttribute / resolveOperator results.
   */
  matchWithOptions?: MatchWithOption[];
  /**
   * Sub-items defined statically for hierarchical dropdown mode.
   * Each item's `type` should equal this option's `id` (required for edit-mode resolution).
   * Takes effect only when `fetchSubAttributes` is not set.
   */
  subAttributes?: ConditionAttributeOption[];
  /**
   * Sub-items loaded from an API for hierarchical dropdown mode.
   * Takes priority over `subAttributes` when both are set.
   * Each parsed item's `type` should equal this option's `id` (required for edit-mode resolution).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchSubAttributes?: FetchOptionsConfig<any, any[], ConditionAttributeOption>;
  /**
   * Optional icon hint for direct-selection leaf items (no sub-items).
   * "info"     → renders an ⓘ icon (e.g. "Role Name")
   * "function" → renders a 📋 icon (e.g. "System or Custom Function")
   */
  itemIcon?: "info" | "function";
  /**
   * Optional hint text shown as a secondary line below the item name in the
   * hierarchical dropdown. Especially useful for direct-selection leaf items
   * to explain what the attribute represents.
   */
  hint?: string;
  /**
   * Optional callback called when a sub-item from this group is selected in
   * hierarchical mode. The return value is shallow-merged into the selected
   * sub-item before it is written to the form state, allowing dynamic
   * computation of valueType, operators, valueOptions, and fetchOptions
   * without requiring each sub-item to carry those fields statically.
   *
   * Example:
   *   resolveSubAttribute: (item) => ({
   *     valueType: item.id.endsWith("_multi") ? "multi-select" : "single-select",
   *   })
   */
  resolveSubAttribute?: (
    selectedItem: ConditionAttributeOption,
  ) => Partial<
    Pick<
      ConditionAttributeOption,
      "valueType" | "operators" | "valueOptions" | "fetchOptions"
    >
  >;
};

export type ConditionLeaf = {
  id: string;
  attribute?: ConditionAttributeOption | null;
  operator?: ConditionOption | null;
  /** Selected Match With option. Only used when config.enableMatchWith is true. */
  matchWith?: MatchWithOption | null;
  value?: string | ConditionOption[] | null;
};

export type ConditionGroupEntry = {
  id: string;
  combinator: "AND" | "OR";
  conditions: ConditionEntry[];
};

export type ConditionEntry = ConditionLeaf | ConditionGroupEntry;

export function isConditionGroup(
  entry: ConditionEntry,
): entry is ConditionGroupEntry {
  return Array.isArray((entry as ConditionGroupEntry).conditions);
}

export type ConditionBuilderLabels = {
  attribute?: string;
  operator?: string;
  matchWith?: string;
  value?: string;
};

export type ConditionBuilderConfig = {
  /**
   * Override the labels shown on the attribute, operator, and value fields.
   * Defaults: { attribute: "Attribute", operator: "Operator", value: "Value" }
   */
  labels?: ConditionBuilderLabels;
  /**
   * The field to group the attributes by.
   * Used to group the attributes by the given field.
   * e.g. "user_attribute" or "agent_attribute"
   */
  attributeGroupField?: string;
  /**
   * Static list of attributes.
   * Used as-is when attributes are known at config definition time.
   */
  attributes?: ConditionAttributeOption[];
  /**
   * Dynamic attributes loaded from an API method.
   * When provided, takes priority over the static `attributes` array.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchAttributes?: FetchOptionsConfig<any, any[], ConditionAttributeOption>;
  /** Global operators list. Used as fallback when no per-attribute override is set. */
  operators: ConditionOption[];
  /**
   * Called when an attribute is selected to dynamically resolve its operators
   * and value configuration. The result is cached by react-query and shared
   * across all fields in the same row — only one API call is made per attribute.
   *
   * Priority chain per field:
   *   resolveAttribute(id) result  >  per-attribute static config  >  global config
   *
   * `attribute` carries the whole selected option, including its `type` (the
   * group it came from). Configs whose attribute *names* are only unique within
   * a group need it: on the edit path `attributeId` is the name, not the id.
   */
  resolveAttribute?: (
    attributeId: string,
    attribute?: ConditionAttributeOption,
  ) => Promise<ResolveAttributeResult>;
  /**
   * Called when an operator is selected to dynamically resolve the value field
   * configuration for that (attribute, operator) pair. The result is cached by
   * react-query. Only value-related fields are supported (valueType, valueOptions,
   * fetchOptions) — operators cannot be overridden here.
   *
   * Priority chain for value config (highest to lowest):
   *   resolveOperator(attributeId, operatorId)  >  resolveAttribute(attributeId)
   *     >  per-attribute static config  >  default ("text")
   */
  resolveOperator?: (
    attributeId: string,
    operatorId: string,
    attribute?: ConditionAttributeOption,
  ) => Promise<ResolveOperatorResult> | undefined;
  allowNestedConditions?: boolean;
  readOnly?: boolean;
  /**
   * When true, every row renders a "Match With" dropdown between Operator and
   * Value. The selected MatchWithOption's value config drives the Value field,
   * and the Value field stays disabled until a Match With option is chosen.
   * When false/omitted, behavior is unchanged (no Match With column).
   *
   * Match With options resolve in priority order:
   *   resolveOperator().matchWithOptions
   *     > resolveAttribute().matchWithOptions
   *     > attribute.matchWithOptions (static)
   */
  enableMatchWith?: boolean;
};

export type ConditionBuilderContextValue = {
  config: ConditionBuilderConfig;
};

export type ConditionBuilderFormValues = {
  combinator: "AND" | "OR";
  conditions: ConditionEntry[];
};
